import {PricingItem} from './pricing-item/pricing-item';
import {ImmutableObject} from '../../utils/immutableObject';
import {Big} from 'big.js';
import {bigToString, MathUtils, parseBig} from '../../utils/math-utils';
import {Corridor} from '../corridor/corridor';
import {EnumCorridorColor, getColorFromString} from '../enum/enum-corridor-color';
import {DevicePricingItem} from "./pricing-item/device-pricing-item";
import {DeviceConsumablePricingItem} from "./pricing-item/device-consumable-pricing-item";
import {PackPricingItem} from "./pricing-item/pack-pricing-item";
import {ConsumablePricingItem} from "./pricing-item/consumable-pricing-item";
import {ExtraFee} from './extra-fee';
import {EnumQuoteType} from '../enum/enum-quote-type';
import {CustomGroup} from './group/custom-group';
import {Coefficient, MultiServiceBoost} from 'rem-pct-engine-core';
import {EnumDisplayMode} from '../enum/enum-display-mode';
import {EnumQuoteServiceType} from '../enum/enum-quote-service-type';
import {EnumQuoteServiceSalesType} from '../enum/enum-quote-service-sales-type';
import {DeviceConsumableWithSuppPricingItem} from './pricing-item/device-consumable-with-supp-pricing-item';
import {EnumPricingItemType} from './pricing-item/pricing-item-type';
import {OptionPricingItem} from './pricing-item/option-pricing-item';

export class QuoteService extends ImmutableObject {

    public readonly code?: string;
    public readonly contractInfoInputs?;
    public readonly lastContractInfoInputs?: any;
    public readonly appliedPrice?: string;
    public readonly appliedPriceOnlyNegociable?: string;
    public readonly appliedPriceOnlyNonNegociable?: string;
    public readonly appliedPriceColor?: string;
    public readonly appliedPriceDevices?: string;
    public readonly appliedPriceConsumables?: string;
    public readonly appliedPriceDevicesColor?: string;
    public readonly appliedPriceConsumablesColor?: string;
    public readonly distance?: string;
    public readonly pct?: string;
    public readonly averagePctCoefficient?: string;
    public readonly deltaPctWithOneMoreService?: string;
    public readonly nonNegociableArticleColor?: string;
    public readonly uniqueReferenceCoefficient?: string;
    public readonly isCmcFullyManaged: boolean = true;

    public readonly isSelected?: boolean;
    public readonly totalLogisticsPrice?: string;
    public readonly factorVt?: string;
    public readonly factorFreeMonth?: string;
    public readonly extraFees?: ExtraFee[];
    public readonly discount?: string;
    public readonly isCustomGroups?: boolean;
    public readonly customGroups?: CustomGroup[];
    public readonly indexCustomGroup?: number;
    public readonly quoteType?: EnumQuoteType;
    public readonly cmcProtocolCode?: string;
    public readonly coefficient?: Coefficient;
    public readonly multiServiceBoost?: MultiServiceBoost;
    public readonly globalCorridor?: Corridor;
    public readonly globalCorridorOnlyNegociable?: Corridor;
    public readonly displayMode?: EnumDisplayMode;
    public corridorConsumables?: Corridor
    public corridorDevices?: Corridor
    public readonly additionalStockAmount?: string;
    public readonly userInputs?: Record<string, any>
    public readonly salesType: EnumQuoteServiceSalesType
    public devicesDiscount?: string;
    public consumablesDiscount?: string;

    constructor(
    ) {
        super();
    }

    get weeklyAmount(): string {
        if(this.displayMode === EnumDisplayMode.MONTHLY)
        {
            return bigToString(parseBig(this.monthlyAmount).div(new Big("4.348")));
        }
        return this.appliedPrice;
    }

    get monthlyAmount(): string {
        if(this.displayMode === EnumDisplayMode.WEEKLY){
            return  bigToString(parseBig(this.appliedPrice).mul(new Big("4.348")));
        }
        return this.appliedPrice;
    }


    private getCorridorDevicesByQuantityAndNegotiable(isWithQty: boolean, isWithNonNegotiableArticle: boolean): Corridor {
        let hasCorridor = false;
        let corridor = new Corridor();
        this.productPricingItems.forEach(pricingItem => {
            if ((pricingItem instanceof DevicePricingItem || pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem ||
                pricingItem instanceof PackPricingItem) && pricingItem.device.corridor) {
                hasCorridor = true;
                if(isWithNonNegotiableArticle || (!isWithNonNegotiableArticle && !pricingItem.device.corridor.isFlat())){
                    if (isWithQty) {
                        corridor = corridor.addCorridor(this.getCorridorWithQuantity(pricingItem.device.corridor, pricingItem.userInputs.quantity));
                    } else {
                        corridor = corridor.addCorridor(pricingItem.device.corridor);
                    }
                }
            }
        })
        return hasCorridor ? corridor : null;
    }



    private getCorridorConsumablesByQuantityAndNegotiable(isWithQty: boolean, isWithNonNegotiableArticle: boolean): Corridor {
        let hasCorridor = false;
        let corridor = new Corridor();
        this.productPricingItems.forEach(pricingItem => {
            if ((pricingItem instanceof ConsumablePricingItem || pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem) && pricingItem.consumable.appliedPriceColor != EnumCorridorColor.GRAY && pricingItem.consumable.corridor) {
                hasCorridor = true;
                if(isWithNonNegotiableArticle || (!isWithNonNegotiableArticle && !pricingItem.consumable.corridor.isFlat())) {
                    if (isWithQty) {
                        corridor = corridor.addCorridor(this.getCorridorWithQuantity(pricingItem.consumable.corridor, !!pricingItem.userInputs.consumableQuantity ? pricingItem.userInputs.consumableQuantity:'0'));
                    } else {
                        corridor = corridor.addCorridor(pricingItem.consumable.corridor);
                    }
                }
            }
        })
        return hasCorridor ? corridor : null;
    }

    getCorridorWithQuantity(corridor: Corridor, quantity: any): Corridor {
        return corridor.copyWith({
            p90: bigToString(MathUtils.maxBig(parseBig(corridor.p90).mul(quantity), new Big(0))),
            p50p90: bigToString(MathUtils.maxBig(parseBig(corridor.p50p90).mul(quantity), new Big(0))),
            p50: bigToString(MathUtils.maxBig(parseBig(corridor.p50).mul(quantity), new Big(0))),
            p10p50: bigToString(MathUtils.maxBig(parseBig(corridor.p10p50).mul(quantity), new Big(0))),
            p10: bigToString(MathUtils.maxBig(parseBig(corridor.p10).mul(quantity), new Big(0))),
        });
    }

    get serviceColor(): EnumCorridorColor {
        return getColorFromString(this.appliedPriceColor);
    }


    getJoinCorridor(){
        return this.getJoinCorridorByQuantityAndNegotiable(true, true);
    }



    private getJoinCorridorByQuantityAndNegotiable(isWithQty: boolean, isWithNonNegotiableArticle: boolean): Corridor {
        const corridorConsumables = this.getCorridorConsumablesByQuantityAndNegotiable(isWithQty, isWithNonNegotiableArticle);
        const corridorDevices = this.getCorridorDevicesByQuantityAndNegotiable(isWithQty, isWithNonNegotiableArticle);

        if (corridorDevices && corridorConsumables) {
            return corridorDevices.addCorridor(corridorConsumables);
        } else if (corridorDevices) {
            return corridorDevices;
        } else if (corridorConsumables) {
            return corridorConsumables;
        } else {
            return null;
        }
    }

    private isTotalNumberOfHoldersValid(): boolean {

        if (this.code == EnumQuoteServiceType.VT && this.userInputs) {
            const hasHolders = this.userInputs?.totalNumberOfHolders > 0;
            const hasPricingItems = this.productPricingItems.length > 0;
            return ((hasHolders) || (!hasHolders && !hasPricingItems));
        }
        return true;
    }

    isQuoteServiceValid(isDisco: boolean): boolean {
        if(this.salesType === EnumQuoteServiceSalesType.NEW) {
            const allArticlesValid = this.productPricingItems.every(pricingItem => pricingItem.isValid);
            const isValidContractInfos = this.contractInfoInputs && this.contractInfoInputs?.isValid;
            const isValidUserInputs = this.isTotalNumberOfHoldersValid();

            if (!isValidUserInputs || !allArticlesValid || isValidContractInfos == false || this.isTotalPriceBelowBlack(isDisco)) {
                return false;
            }
            return this.isCustomGroupsValid();
        }
        return true;
    }

    isTotalPriceBelowBlack(isDisco: boolean): boolean {

        if(this.cmcProtocolCode == '111111' || isDisco){
            return false;
        }

        const corridor = this.getJoinCorridor();
        return  corridor && parseBig(this.appliedPrice).lt(parseBig(corridor.p10));
    }

    isCustomGroupsValid(): boolean {

        return !this.isCustomGroups ||  this.customGroups.length == 0 || this.customGroups.every(customGroup => customGroup.isValid);
    }

    get productPricingItems(): PricingItem[] {
        return this.allPricingItems.filter(pricingItem => pricingItem.pricingItemType !== EnumPricingItemType.OPTION);
    }

    get options(): OptionPricingItem[] {
        return this.allPricingItems.filter(pricingItem => pricingItem.pricingItemType === EnumPricingItemType.OPTION) as OptionPricingItem[];
    }

    get allPricingItems(): PricingItem[] {
        return this.customGroups.reduce((accumulator, customGroup) => accumulator.concat(customGroup.pricingItems), []);
    }

    getNonNegotiableAppliedPrice() {
        let flatAppliedPrices = new Big(0);
        this.productPricingItems.forEach((pricingItem) => {
            if ((pricingItem instanceof DevicePricingItem || pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem ||
                    pricingItem instanceof PackPricingItem)
                && pricingItem.device.corridor?.isFlat()) {
                flatAppliedPrices = flatAppliedPrices.add(
                    parseBig(pricingItem.device.appliedPrice).mul(parseBig(pricingItem.userInputs.quantity)));
            }
            if ((pricingItem instanceof ConsumablePricingItem || pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem)
                && pricingItem.consumable.corridor?.isFlat()) {
                flatAppliedPrices = flatAppliedPrices.add(
                    parseBig(pricingItem.consumable.appliedPrice).mul(parseBig(pricingItem.userInputs.consumableQuantity)));
            }
        });
        return flatAppliedPrices;
    }

    get nonNegociableArticlesMonthlyAmount(): string {
        let nonNegotiableAppliedPrice = this.getNonNegotiableAppliedPrice();
        if(this.displayMode === EnumDisplayMode.WEEKLY){
            return  bigToString(nonNegotiableAppliedPrice.mul(new Big("4.348")));
        }
        return bigToString(nonNegotiableAppliedPrice);
    }

    get negociableArticlesMonthlyAmount(): string {
        return bigToString(new Big(this.monthlyAmount).minus(this.nonNegociableArticlesMonthlyAmount));
    }

    hasPricingItems(): boolean {
        return this.productPricingItems.length >0
    }

    hasPricingItemsWithQuantity(): boolean {
        return this.productPricingItems.some(pricingItem => parseBig(pricingItem.userInputs.quantity).gt(0) || parseBig(pricingItem.userInputs.consumableQuantity).gt(0))
    }

    hasPricingItemsWithFranchiseConso(): boolean {
        return this.productPricingItems.some(pricingItem => pricingItem.hasFranchiseConso());
    }

    isServicePriced(): boolean {
        return this.hasPricingItemsWithQuantity() || this.hasPricingItemsWithFranchiseConso();
    }

}
